import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { apiClient } from "@/core/api/ApiClient";
import {
  ChatParticipantAddManyResultDto,
  ChatParticipantDto,
  ChatParticipantJoinResultDto,
  ChatParticipantLeaveResultDto,
  ChatParticipantOnlineStatus,
  ChatParticipantRemoveResultDto,
  ChatParticipantRemovedDto,
  ChatParticipantsAddedDto,
  PaginationDtoOfChatParticipantCandidateDto,
  PaginationDtoOfChatParticipantDto,
} from "@/core/api/generated/v1";
import { AppThunkAction } from "@/store";

import * as chatUserSettingsSlice from "./chatUserSettingsSlice";
import * as chatsSlice from "./chatsSlice";

interface StoreParticipantStatus {
  onlineStatus?: ChatParticipantOnlineStatus;
}

export interface ChatParticipantsState {
  loading: {
    getPaginated?: Record<string, boolean>;
    getChatParticipantCandidates?: boolean;
    joinChat?: boolean;
    leaveChat?: boolean;
    addChatParticipants?: Record<string, boolean>;
    removeChatParticipant?: Record<string, boolean>;
  };
  paginatedChatParticipantMap: Record<string, PaginationDtoOfChatParticipantDto | undefined>; // chatId:participant
  paginatedChatParticipantCandidates?: PaginationDtoOfChatParticipantCandidateDto;
  participantStatusMap: Record<string, Record<string, StoreParticipantStatus | undefined>>; // chatId:{participantId:status}

  // computed
}

const initialState: ChatParticipantsState = {
  loading: {
    getPaginated: {},
  },
  paginatedChatParticipantMap: {},
  paginatedChatParticipantCandidates: undefined,
  participantStatusMap: {},
};

const tenantsSlice = createSlice({
  name: "chatParticipants",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<ChatParticipantsState["loading"]>) => {
      state.loading = {
        ...state.loading,
        ...action.payload,
      };
    },
    resetChatParticipants: (state, action: PayloadAction<{ chatId?: string | null }>) => {
      if (action.payload.chatId) {
        state.paginatedChatParticipantMap[action.payload.chatId] = undefined;
        state.participantStatusMap[action.payload.chatId] = {};
      }
    },
    _chatParticipantsFetched: (
      state,
      action: PayloadAction<{ chatId: string; data: PaginationDtoOfChatParticipantDto }>,
    ) => {
      state.paginatedChatParticipantMap[action.payload.chatId] = action.payload.data;
    },
    _chatParticipantCandidatesFetched: (
      state,
      action: PayloadAction<PaginationDtoOfChatParticipantCandidateDto>,
    ) => {
      state.paginatedChatParticipantCandidates = action.payload;
    },
    _chatParticipantJoined: (
      state,
      action: PayloadAction<{ chatId: string; participant: ChatParticipantDto }>,
    ) => {
      // add or update participant
      const paginated = state.paginatedChatParticipantMap[action.payload.chatId];
      if (paginated) {
        const index = paginated.items!.findIndex((x) => x.id === action.payload.participant.id);
        if (index === -1) {
          paginated.items!.push(action.payload.participant);
        } else {
          paginated.items!.splice(index, 1, action.payload.participant);
        }
      }
    },
    chatParticipantJoined: (
      state,
      action: PayloadAction<{ chatId: string; participant: ChatParticipantDto }>,
    ) => {
      // add or update participant
      const paginated = state.paginatedChatParticipantMap[action.payload.chatId];
      if (paginated) {
        const index = paginated.items!.findIndex((x) => x.id === action.payload.participant.id);
        if (index === -1) {
          paginated.items!.push(action.payload.participant);
        } else {
          paginated.items!.splice(index, 1, action.payload.participant);
        }
      }
    },
    _chatParticipantLeft: (
      state,
      action: PayloadAction<{ chatId: string; participant: ChatParticipantDto }>,
    ) => {
      // update participant
      const paginated = state.paginatedChatParticipantMap[action.payload.chatId];
      if (paginated) {
        const index = paginated.items!.findIndex((x) => x.id === action.payload.participant.id);
        if (index !== -1) {
          paginated.items!.splice(index, 1, action.payload.participant);
        }
      }
    },
    chatParticipantLeft: (
      state,
      action: PayloadAction<{ chatId: string; participant: ChatParticipantDto }>,
    ) => {
      // update participant
      const paginated = state.paginatedChatParticipantMap[action.payload.chatId];
      if (paginated) {
        const index = paginated.items!.findIndex((x) => x.id === action.payload.participant.id);
        if (index !== -1) {
          paginated.items!.splice(index, 1, action.payload.participant);
        }
      }
    },
    _chatParticipantsAdded: (
      state,
      action: PayloadAction<{ chatId: string; participants: ChatParticipantDto[] }>,
    ) => {
      // add or update participants
      const paginated = state.paginatedChatParticipantMap[action.payload.chatId];
      if (paginated) {
        action.payload.participants.forEach((participant) => {
          const index = paginated.items!.findIndex((x) => x.id === participant.id);
          if (index === -1) {
            paginated.items!.push(participant);
          } else {
            paginated.items!.splice(index, 1, participant);
          }
        });
      }
    },
    chatParticipantsAdded: (state, action: PayloadAction<ChatParticipantsAddedDto>) => {
      // add or update participants
      const paginated = state.paginatedChatParticipantMap[action.payload.chatId || ""];
      if (paginated) {
        action.payload.participants?.forEach((participant) => {
          const index = paginated.items!.findIndex((x) => x.id === participant.id);
          if (index === -1) {
            paginated.items!.push(participant);
          } else {
            paginated.items!.splice(index, 1, participant);
          }
        });
      }
    },
    _chatParticipantRemoved: (
      state,
      action: PayloadAction<{ chatId: string; participant: ChatParticipantDto }>,
    ) => {
      // update participant
      const paginated = state.paginatedChatParticipantMap[action.payload.chatId];
      if (paginated) {
        const index = paginated.items!.findIndex((x) => x.id === action.payload.participant.id);
        if (index !== -1) {
          paginated.items!.splice(index, 1, action.payload.participant);
        }
      }
    },
    chatParticipantRemoved: (state, action: PayloadAction<ChatParticipantRemovedDto>) => {
      // update participant
      const paginated = state.paginatedChatParticipantMap[action.payload.chatId || ""];
      if (paginated && action.payload.participant) {
        const index = paginated.items!.findIndex((x) => x.id === action.payload.participant?.id);
        if (index !== -1) {
          paginated.items!.splice(index, 1, action.payload.participant);
        }
      }
    },
    updateParticipantStatus: (
      state,
      action: PayloadAction<{
        chatId?: string | null;
        participantId?: string | null;
        status: StoreParticipantStatus;
      }>,
    ) => {
      if (action.payload.chatId && action.payload.participantId) {
        state.participantStatusMap[action.payload.chatId] =
          state.participantStatusMap[action.payload.chatId] || {};
        state.participantStatusMap[action.payload.chatId][action.payload.participantId] =
          state.participantStatusMap[action.payload.chatId][action.payload.participantId] || {};
        state.participantStatusMap[action.payload.chatId][action.payload.participantId] = {
          ...state.participantStatusMap[action.payload.chatId][action.payload.participantId],
          ...action.payload.status,
        };
      }
    },
    /** Updates status for all participants in all chats. */
    updateAllParticipantsStatusInAllChats: (
      state,
      action: PayloadAction<{
        status: StoreParticipantStatus;
      }>,
    ) => {
      for (const chatId in state.participantStatusMap) {
        state.participantStatusMap[chatId] = state.participantStatusMap[chatId] || {};
        for (const participantId in state.participantStatusMap[chatId]) {
          state.participantStatusMap[chatId][participantId] =
            state.participantStatusMap[chatId][participantId] || {};
          state.participantStatusMap[chatId][participantId] = {
            ...state.participantStatusMap[chatId][participantId],
            ...action.payload.status,
          };
        }
      }
    },
    /** Updates status for the participant in all chats. */
    updateParticipantStatusInAllChats: (
      state,
      action: PayloadAction<{
        participantId?: string | null;
        status: StoreParticipantStatus;
      }>,
    ) => {
      if (action.payload.participantId) {
        for (const chatId in state.participantStatusMap) {
          state.participantStatusMap[chatId] = state.participantStatusMap[chatId] || {};
          state.participantStatusMap[chatId][action.payload.participantId] =
            state.participantStatusMap[chatId][action.payload.participantId] || {};
          state.participantStatusMap[chatId][action.payload.participantId] = {
            ...state.participantStatusMap[chatId][action.payload.participantId],
            ...action.payload.status,
          };
        }
      }
    },
  },
});

export const {
  setLoading,
  resetChatParticipants,
  _chatParticipantsFetched,
  _chatParticipantCandidatesFetched,
  _chatParticipantJoined,
  chatParticipantJoined,
  _chatParticipantLeft,
  chatParticipantLeft,
  _chatParticipantsAdded,
  chatParticipantsAdded,
  _chatParticipantRemoved,
  chatParticipantRemoved,
  updateParticipantStatus,
  updateAllParticipantsStatusInAllChats,
  updateParticipantStatusInAllChats,
} = tenantsSlice.actions;

export const chatParticipantsReducer = tenantsSlice.reducer;

export const getChatParticipants = (
  ...arg: Parameters<typeof apiClient.chatParticipantsApi.apiV1ChatsChatIdParticipantsGetPost>
): AppThunkAction<Promise<PaginationDtoOfChatParticipantDto>> => {
  const chatId = arg[0].chatId;

  return async (dispatch) => {
    dispatch(
      setLoading({
        getPaginated: {
          [chatId]: true,
        },
      }),
    );

    try {
      const response = await apiClient.chatParticipantsApi.apiV1ChatsChatIdParticipantsGetPost(
        ...arg,
      );
      await dispatch(
        _chatParticipantsFetched({
          chatId,
          data: response.data,
        }),
      );
      return response.data;
    } finally {
      dispatch(
        setLoading({
          getPaginated: {
            [chatId]: false,
          },
        }),
      );
    }
  };
};

export const getAllChatParticipants = (params: {
  chatId: string;
}): AppThunkAction<Promise<PaginationDtoOfChatParticipantDto>> => {
  return async (dispatch) => {
    return await dispatch(
      getChatParticipants({
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        chatId: params.chatId,
        chatParticipantGetPaginatedDto: {
          offset: 0,
          limit: 100,
          status: undefined, // get all participants (including inactive) to correctly displayed chat items authors
        },
      }),
    );
  };
};

export const getChatParticipantCandidates = (
  ...arg: Parameters<typeof apiClient.chatParticipantsApi.apiV1ChatsChatIdParticipantsCandidatesGet>
): AppThunkAction<Promise<PaginationDtoOfChatParticipantCandidateDto>> => {
  return async (dispatch) => {
    dispatch(
      setLoading({
        getChatParticipantCandidates: true,
      }),
    );

    try {
      const response =
        await apiClient.chatParticipantsApi.apiV1ChatsChatIdParticipantsCandidatesGet(...arg);
      await dispatch(_chatParticipantCandidatesFetched(response.data));
      return response.data;
    } finally {
      dispatch(
        setLoading({
          getChatParticipantCandidates: false,
        }),
      );
    }
  };
};

export const joinChat = (
  ...arg: Parameters<typeof apiClient.chatParticipantsApi.apiV1ChatsChatIdParticipantsJoinPost>
): AppThunkAction<Promise<ChatParticipantJoinResultDto>> => {
  return async (dispatch) => {
    dispatch(
      setLoading({
        joinChat: true,
      }),
    );

    try {
      const response = await apiClient.chatParticipantsApi.apiV1ChatsChatIdParticipantsJoinPost(
        ...arg,
      );
      if (response.data.chat && response.data.participant) {
        await dispatch(chatsSlice.joinedChat(response.data.chat));
        await dispatch(chatsSlice.chatFetched(response.data.chat));
        await dispatch(
          _chatParticipantJoined({
            chatId: response.data.chat.id!,
            participant: response.data.participant,
          }),
        );
        await dispatch(getAllChatParticipants({ chatId: response.data.chat.id! }));
        dispatch(
          chatUserSettingsSlice.getChatUserSettings({
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            chatId: response.data.chat.id!,
          }),
        );
      }

      return response.data;
    } finally {
      dispatch(
        setLoading({
          joinChat: false,
        }),
      );
    }
  };
};

export const leaveChat = (
  ...arg: Parameters<typeof apiClient.chatParticipantsApi.apiV1ChatsChatIdParticipantsLeavePost>
): AppThunkAction<Promise<ChatParticipantLeaveResultDto>> => {
  return async (dispatch) => {
    dispatch(
      setLoading({
        leaveChat: true,
      }),
    );

    try {
      const response = await apiClient.chatParticipantsApi.apiV1ChatsChatIdParticipantsLeavePost(
        ...arg,
      );
      if (response.data.chat) {
        await dispatch(chatsSlice.chatFetched(response.data.chat));
      }
      if (response.data.participant) {
        await dispatch(
          _chatParticipantLeft({
            chatId: response.data.chat?.id || arg[0].chatId,
            participant: response.data.participant,
          }),
        );
      }

      return response.data;
    } finally {
      dispatch(
        setLoading({
          leaveChat: false,
        }),
      );
    }
  };
};

export const addChatParticipants = (
  ...arg: Parameters<typeof apiClient.chatParticipantsApi.apiV1ChatsChatIdParticipantsPost>
): AppThunkAction<Promise<ChatParticipantAddManyResultDto>> => {
  const chatId = arg[0].chatId;

  return async (dispatch) => {
    dispatch(
      setLoading({
        addChatParticipants: {
          [chatId]: true,
        },
      }),
    );

    try {
      const response = await apiClient.chatParticipantsApi.apiV1ChatsChatIdParticipantsPost(...arg);
      if (response.data.chat) {
        await dispatch(chatsSlice.chatFetched(response.data.chat));
      }
      if (response.data.participants) {
        await dispatch(
          _chatParticipantsAdded({
            chatId: chatId,
            participants: response.data.participants,
          }),
        );
      }
      return response.data;
    } finally {
      dispatch(
        setLoading({
          addChatParticipants: {
            [chatId]: false,
          },
        }),
      );
    }
  };
};

export const removeChatParticipant = (
  ...arg: Parameters<
    typeof apiClient.chatParticipantsApi.apiV1ChatsChatIdParticipantsParticipantIdDelete
  >
): AppThunkAction<Promise<ChatParticipantRemoveResultDto>> => {
  const chatId = arg[0].chatId;
  const participantId = arg[0].participantId;

  return async (dispatch) => {
    dispatch(
      setLoading({
        removeChatParticipant: {
          [participantId]: true,
        },
      }),
    );

    try {
      const response =
        await apiClient.chatParticipantsApi.apiV1ChatsChatIdParticipantsParticipantIdDelete(...arg);
      if (response.data.chat) {
        await dispatch(chatsSlice.chatFetched(response.data.chat));
      }
      if (response.data.participant) {
        await dispatch(
          _chatParticipantRemoved({
            chatId: chatId,
            participant: response.data.participant,
          }),
        );
      }
      return response.data;
    } finally {
      dispatch(
        setLoading({
          removeChatParticipant: {
            [participantId]: false,
          },
        }),
      );
    }
  };
};
